import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';

import {
  DeadlineStatusList,
  NoDeadlineStatus,
  TicketStatesGlobal,
  AggregatedTicketStates,
} from '../../../../components/ticket/ticket.constants';

// We use 99, because angular translate messageformat
// could not handle -1.
const NoDeadlineDefinedIndicator = 99;

export class TicketReader extends InjectableClass {
  constructor(ticket) {
    super({ ticket });
  }

  get branchId() {
    return this.ticket.branchId;
  }

  get client() {
    return this.ticket.client;
  }

  get created() {
    return new Date(this.ticket.createdOffsetDateTime);
  }

  get nextDeadline() {
    return this.ticket.nextDeadline;
  }

  get nextDeadlineInBankDays() {
    if (
      this.nextDeadline === null ||
      _.includes(NoDeadlineStatus, this.status) ||
      this.status === TicketStatesGlobal.REQUEST_COMPLETED
    ) {
      return NoDeadlineDefinedIndicator;
    }
    return this.ticket.numberOfBankDaysTillNextDeadline;
  }

  get deadlineStatus() {
    const { nextDeadlineInBankDays } = this;

    if (nextDeadlineInBankDays === NoDeadlineDefinedIndicator) {
      // no deadline
      return DeadlineStatusList.none;
    }
    if (nextDeadlineInBankDays > 1) {
      // > 2 days
      return DeadlineStatusList.week;
    }
    if (nextDeadlineInBankDays > 0) {
      // > 1 day
      return DeadlineStatusList.tomorrow;
    }
    if (nextDeadlineInBankDays === 0) {
      // <= 1 day
      return DeadlineStatusList.today;
    }

    // overdue
    return DeadlineStatusList.overdue;
  }

  get status() {
    return this.ticket.status;
  }

  get statusChangedAt() {
    return this.ticket.statusChangedAt;
  }

  get closed() {
    return this.ticket.closed;
  }

  get aggregatedStatus() {
    const { status } = this;
    return AggregatedTicketStates[status] || status;
  }

  get displayStatus() {
    return `app.ticket.status.${this.status}`;
  }

  get advisor() {
    const { advisor } = this.ticket;
    return `${advisor.firstname} ${advisor.lastname}`;
  }

  get inCharge() {
    return this.ticket.inCharge;
  }

  get dueDate() {
    // TODO: Add due date from ticket model
    const due = new Date(+new Date() - 4 * 24 * 60 * 60 * 1000);
    const days = parseInt((new Date() - due) / (24 * 3600 * 1000));
    return `${days}`;
  }

  get role() {
    return this.ticket.inCharge;
  }

  get details() {
    return 'ticketdetails';
  }

  get id() {
    return this.ticket.id;
  }

  get rejections() {
    return this.ticket.rejections;
  }

  get transferringIban() {
    return this.ticket.oldIban;
  }

  get receivingIban() {
    return this.ticket.newIban;
  }

  get newBankName() {
    return this.ticket.newBankName;
  }

  get oldBankName() {
    return this.ticket.oldBankName;
  }

  get isCustomerAnEmployee() {
    return this.ticket.isCustomerTenantEmployee;
  }

  get archivedAt() {
    return this.ticket.archivedAt;
  }

  get archived() {
    return this.ticket.archived;
  }

  get isArchivable() {
    return this.ticket.isArchivable;
  }

  get requestType() {
    // The ``s are just to make sure we have a string
    return `${this.ticket.requestType}`.toLowerCase();
  }

  get guidanceRequest() {
    return this.ticket.guidanceRequest;
  }

  get guidanceType() {
    if (this.ticket.guidanceRequest) {
      return this.ticket.guidanceRequest.type;
    }
    return null;
  }

  get warrantShipmentSkipped() {
    return this.ticket.warrantShipmentSkipped;
  }

  get locked() {
    return this.lockExpiresAt && new Date(this.lockExpiresAt) > new Date();
  }

  get lockedAt() {
    return this.ticket.lockedAt;
  }

  get lockedBy() {
    return this.ticket.lockedBy;
  }

  get lock() {
    return {
      createdAt: this.lockedAt,
      owner: this.lockedBy,
      id: this.id,
    };
  }

  get lockExpiresAt() {
    return this.ticket.lockExpiresAt;
  }

  get internal() {
    return this.ticket.internal;
  }

  get remindersPaused() {
    return this.ticket.remindersPaused;
  }

  get isNachlass() {
    return this.ticket.isNachlass;
  }
}
